import {
  useGetApiSettingsHomeLocation,
  useGetApiUtilitiesConfig,
  useGetApiUtilitiesOverview,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import {
  AlertCircle,
  Clock,
  Settings,
  Upload,
  ChevronRight,
  TrendingUp,
  Coins,
} from 'lucide-react';
import { useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui';

type RangePreset = 'today' | '7d' | '30d' | 'mtd' | 'ytd';

const RANGE_OPTIONS: { value: RangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: 'mtd', label: 'MTD' },
  { value: 'ytd', label: 'YTD' },
];

const VALID_PRESETS: RangePreset[] = ['today', '7d', '30d', 'mtd', 'ytd'];

function isValidPreset(value: string | null): value is RangePreset {
  return value !== null && VALID_PRESETS.includes(value as RangePreset);
}

// Number formatters
const CONSUMPTION_FORMATTER = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatConsumption(value?: number): string {
  if (value === undefined || value === null) return '—';
  return CONSUMPTION_FORMATTER.format(value);
}

function formatCost(value?: number, currencyCode?: string): string {
  if (value === undefined || value === null) return '—';
  if (currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(value);
    } catch {
      // Fall back to plain number if currency code is invalid
    }
  }
  return value.toFixed(2);
}

export function UtilitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse range from URL, defaulting to '30d'
  const range = useMemo((): RangePreset => {
    const rangeParam = searchParams.get('range');
    return isValidPreset(rangeParam) ? rangeParam : '30d';
  }, [searchParams]);

  const handleRangeChange = useCallback(
    (newRange: RangePreset) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('range', newRange);
        return next;
      });
    },
    [setSearchParams]
  );

  const {
    data: homeLocationData,
    isLoading: isLoadingHome,
    isError: isHomeError,
  } = useGetApiSettingsHomeLocation();
  const homeLocation = homeLocationData?.location;
  const hasTimezone = Boolean(homeLocation?.timezone && homeLocation.timezone.trim() !== '');

  // Only fetch config if timezone is configured (for checking if categories exist)
  const {
    data: configData,
    isLoading: isLoadingConfig,
    isError: isConfigError,
  } = useGetApiUtilitiesConfig({
    query: {
      enabled: hasTimezone,
    },
  });
  const config = configData?.config;
  const hasCategories = (config?.categories ?? []).length > 0;

  // Fetch overview data with range preset
  const {
    data: overviewData,
    isLoading: isLoadingOverview,
    isError: isOverviewError,
  } = useGetApiUtilitiesOverview(
    { preset: range },
    {
      query: {
        enabled: hasTimezone && hasCategories,
      },
    }
  );
  const categories = useMemo(() => overviewData?.categories ?? [], [overviewData?.categories]);
  const currencyCode = overviewData?.currencyCode;

  // Calculate total cost across all categories
  const totalCost = useMemo(() => {
    return categories.reduce((sum, cat) => sum + (cat.cost ?? 0), 0);
  }, [categories]);

  const isLoading =
    isLoadingHome ||
    (hasTimezone && isLoadingConfig) ||
    (hasTimezone && hasCategories && isLoadingOverview);
  const isError =
    isHomeError ||
    (hasTimezone && isConfigError) ||
    (hasTimezone && hasCategories && isOverviewError);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Utilities
        </h1>
        <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
          Consumption and cost insights
        </p>
      </div>

      {/* Range selector - only show when we have categories */}
      {hasTimezone && hasCategories && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-zinc-500">Range</span>
          <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-zinc-800">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleRangeChange(option.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  range === option.value
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="rounded-lg bg-red-50 p-6 text-center dark:bg-red-500/10">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-sm text-red-600 dark:text-red-400">Failed to load resource data</p>
        </div>
      )}

      {/* Missing timezone empty state */}
      {!isLoading && !isError && !hasTimezone && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/10">
            <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Set your home timezone
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-zinc-500">
            The Utility Dashboard needs a timezone configured to correctly determine day and month
            boundaries for usage tracking and cost calculations.
          </p>
          <Link to={ROUTES.SETTINGS_HOME_LOCATION}>
            <Button className="mt-6">Configure Home Location</Button>
          </Link>
        </div>
      )}

      {/* No categories empty state */}
      {!isLoading && !isError && hasTimezone && !hasCategories && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-500/10">
            <Settings className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            No resource categories yet
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-zinc-500">
            Configure resource categories to start tracking your electricity, gas, water, and other
            utilities.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link to={ROUTES.UTILITIES_MANAGE}>
              <Button leftIcon={<Settings className="h-4 w-4" />}>Configure resources</Button>
            </Link>
            <Link to={ROUTES.UTILITIES_MANAGE}>
              <Button variant="outline" leftIcon={<Upload className="h-4 w-4" />}>
                Import config
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Total cost summary */}
      {!isLoading && !isError && hasTimezone && hasCategories && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-zinc-500">Total Cost</p>
              <p className="mt-1 text-3xl font-semibold tabular-nums text-gray-900 dark:text-white">
                {formatCost(totalCost, currencyCode)}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
              <Coins className="h-6 w-6 text-emerald-500" />
            </div>
          </div>
        </div>
      )}

      {/* Category cards */}
      {!isLoading && !isError && hasTimezone && hasCategories && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <Link
              key={category.categoryId}
              to={`${ROUTES.UTILITIES_CATEGORY.replace(':categoryId', category.categoryId ?? '')}?range=${range}`}
              className="group rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-sm dark:border-white/[0.06] dark:bg-[#18181b] dark:hover:border-white/[0.1]"
            >
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {category.name}
                </h3>
                <ChevronRight className="h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-0.5 dark:text-zinc-600" />
              </div>

              <div className="mt-4 space-y-3">
                {/* Consumption */}
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10">
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-zinc-500">Consumption</p>
                    <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                      {formatConsumption(category.consumption)}{' '}
                      <span className="font-normal text-gray-500 dark:text-zinc-500">
                        {category.unitSymbol}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Cost */}
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10">
                    <Coins className="h-4 w-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-zinc-500">Cost</p>
                    <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                      {formatCost(category.cost, currencyCode)}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Manage link (when categories exist) */}
      {!isLoading && !isError && hasTimezone && hasCategories && (
        <div className="flex justify-end">
          <Link to={ROUTES.UTILITIES_MANAGE}>
            <Button variant="outline" leftIcon={<Settings className="h-4 w-4" />}>
              Manage Utilities
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
